import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges, NgZone, AfterViewChecked, ElementRef, OnDestroy } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import embed from 'vega-embed';

interface ToolCall {
  name: string;
  icon: SafeHtml;
  paramPreview: string;
  params: { key: string; value: string }[];
  result?: string;
  isOpen: boolean;
  /** The model's own one-line description of the call (Bash/Agent `description`) —
   *  what the CLI prints as the row header. Absent for tools without one. */
  title?: string;
  /** When this call first appeared in the stream (elapsed-time display). */
  startedAt?: number;
}

interface ToolGroup {
  tools: ToolCall[];
  isOpen: boolean;
}

interface AskQuestion {
  header?: string;
  question: string;
  /** Dialog body above the question (a permission prompt's command + guard reason). */
  context?: string;
  options: { label: string; description?: string }[];
  multiSelect?: boolean;
}

interface ContentPart {
  type: 'text' | 'tool' | 'tool-group' | 'ask' | 'thinking';
  content?: string;
  tool?: ToolCall;
  toolGroup?: ToolGroup;
  questions?: AskQuestion[];
}

@Component({
    selector: 'app-tool-message',
    templateUrl: './tool-message.component.html',
    styleUrls: ['./tool-message.component.scss'],
    standalone: false
})
export class ToolMessageComponent implements OnChanges, AfterViewChecked, OnDestroy {
  @Input() content: string = '';
  @Input() isStreaming: boolean = false;
  @Input() hideStreamingDots: boolean = false;
  /** Persisted answer label for an AskUserQuestion in this turn — renders as the
   *  selected option (survives the streaming→final component swap and reloads). */
  @Input() presetAnswer?: string;
  @Output() openFile = new EventEmitter<{ path: string; title: string }>();
  /** Emitted when the user clicks an option on an interactive AskUserQuestion. */
  @Output() answer = new EventEmitter<{ question: string; answer: string }>();

  parts: ContentPart[] = [];
  private wasTuiMode = false;
  private viewCheckedCount = 0;
  /** First-seen time per tool ordinal — parts are rebuilt on every chunk, so the
   *  timestamps live here and are re-stamped onto the fresh ToolCall objects. */
  private toolStart = new Map<number, number>();
  private lastContent = '';
  /** 1 s tick while a group is working so the elapsed seconds advance. */
  private ticker: ReturnType<typeof setInterval> | null = null;
  /** Questions answered in this message, keyed by question text → chosen label. */
  private answered = new Map<string, string>();

  // SVG icons for different tool types
  private iconMap: Record<string, string> = {
    'Read': '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16"><path fill="currentColor" d="M19 2H6c-1.206 0-3 .799-3 3v14c0 2.201 1.794 3 3 3h15v-2H6.012C5.55 19.988 5 19.806 5 19s.55-.988 1.012-1H21V4c0-1.103-.897-2-2-2zm0 14H5V5c0-.806.55-.988 1-1h13v12z"/></svg>',
    'Write': '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16"><path fill="currentColor" d="M20.71 7.04c.39-.39.39-1.04 0-1.41l-2.34-2.34c-.37-.39-1.02-.39-1.41 0l-1.84 1.83 3.75 3.75M3 17.25V21h3.75L17.81 9.93l-3.75-3.75L3 17.25z"/></svg>',
    'Edit': '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16"><path fill="currentColor" d="M20.71 7.04c.39-.39.39-1.04 0-1.41l-2.34-2.34c-.37-.39-1.02-.39-1.41 0l-1.84 1.83 3.75 3.75M3 17.25V21h3.75L17.81 9.93l-3.75-3.75L3 17.25z"/></svg>',
    'Bash': '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16"><path fill="currentColor" d="M20 19V7H4v12h16m0-16a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h16m-7 14v-2h5v2h-5m-3.42-4L5.57 9H8.4l3.3 3.3c.39.39.39 1.03 0 1.42L8.42 17H5.59l4-4z"/></svg>',
    'Grep': '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16"><path fill="currentColor" d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/></svg>',
    'Glob': '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16"><path fill="currentColor" d="M10 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2h-8l-2-2z"/></svg>',
    'WebFetch': '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16"><path fill="currentColor" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/></svg>',
    'WebSearch': '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16"><path fill="currentColor" d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/></svg>',
    'Task': '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16"><path fill="currentColor" d="M12 2a2 2 0 0 1 2 2c0 .74-.4 1.39-1 1.73V7h1a7 7 0 0 1 7 7h1a1 1 0 0 1 1 1v3a1 1 0 0 1-1 1h-1v1a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-1H2a1 1 0 0 1-1-1v-3a1 1 0 0 1 1-1h1a7 7 0 0 1 7-7h1V5.73c-.6-.34-1-.99-1-1.73a2 2 0 0 1 2-2M7.5 13A2.5 2.5 0 0 0 5 15.5 2.5 2.5 0 0 0 7.5 18a2.5 2.5 0 0 0 2.5-2.5A2.5 2.5 0 0 0 7.5 13zm9 0a2.5 2.5 0 0 0-2.5 2.5 2.5 2.5 0 0 0 2.5 2.5 2.5 2.5 0 0 0 2.5-2.5 2.5 2.5 0 0 0-2.5-2.5z"/></svg>',
    'TodoWrite': '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16"><path fill="currentColor" d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-9 14l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/></svg>',
    'LSP': '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16"><path fill="currentColor" d="M9.4 16.6L4.8 12l4.6-4.6L8 6l-6 6 6 6 1.4-1.4zm5.2 0l4.6-4.6-4.6-4.6L16 6l6 6-6 6-1.4-1.4z"/></svg>',
    'AskUserQuestion': '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16"><path fill="currentColor" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 17h-2v-2h2v2zm2.07-7.75l-.9.92C13.45 12.9 13 13.5 13 15h-2v-.5c0-1.1.45-2.1 1.17-2.83l1.24-1.26c.37-.36.59-.86.59-1.41 0-1.1-.9-2-2-2s-2 .9-2 2H8c0-2.21 1.79-4 4-4s4 1.79 4 4c0 .88-.36 1.68-.93 2.25z"/></svg>',
    'ScheduleWakeup': '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16"><path fill="currentColor" d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z"/></svg>',
    'default': '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16"><path fill="currentColor" d="M22.7 19l-9.1-9.1c.9-2.3.4-5-1.5-6.9-2-2-5-2.4-7.4-1.3L9 6 6 9 1.6 4.7C.4 7.1.9 10.1 2.9 12.1c1.9 1.9 4.6 2.4 6.9 1.5l9.1 9.1c.4.4 1 .4 1.4 0l2.3-2.3c.5-.4.5-1.1.1-1.4z"/></svg>'
  };

  constructor(private sanitizer: DomSanitizer, private ngZone: NgZone, private el: ElementRef) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['content']) {
      // A new message (not a longer version of the same stream) resets the clocks.
      if (!this.content.startsWith(this.lastContent)) this.toolStart.clear();
      this.lastContent = this.content;
      this.parseContent();
      this.stampToolStarts();
      this.viewCheckedCount = 0;
    }
    this.syncTicker();
  }

  ngOnDestroy(): void {
    if (this.ticker) { clearInterval(this.ticker); this.ticker = null; }
  }

  private stampToolStarts(): void {
    const now = Date.now();
    let k = 0;
    const stamp = (t: ToolCall) => {
      const seen = this.toolStart.get(k);
      t.startedAt = seen ?? now;
      if (seen === undefined) this.toolStart.set(k, now);
      k++;
    };
    for (const p of this.parts) {
      if (p.type === 'tool' && p.tool) stamp(p.tool);
      else if (p.type === 'tool-group' && p.toolGroup) p.toolGroup.tools.forEach(stamp);
    }
  }

  private syncTicker(): void {
    const want = this.isStreaming && this.hasWorkingGroup;
    if (want && !this.ticker) {
      this.ticker = setInterval(() => { /* zone-run: triggers change detection */ }, 1000);
    } else if (!want && this.ticker) {
      clearInterval(this.ticker); this.ticker = null;
    }
  }

  /** The icon strip is texture, not information: a long run shows the first
   *  few icons and a "+N" chip so the label (what is running, or the tool mix)
   *  keeps the row. Seventy icons once squeezed the label into a five-character
   *  column (games, 2026-09-17). */
  static readonly ICON_CAP = 12;
  iconStrip(group: ToolGroup): ToolCall[] {
    return group.tools.length > ToolMessageComponent.ICON_CAP
      ? group.tools.slice(0, ToolMessageComponent.ICON_CAP)
      : group.tools;
  }
  hiddenIconCount(group: ToolGroup): number {
    return Math.max(0, group.tools.length - ToolMessageComponent.ICON_CAP);
  }

  /** Collapsed header text for a tool group. While working: the running call's
   *  own description (or name + argument) with elapsed seconds — the same line
   *  the CLI shows. Finished + collapsed: the tool mix ("Ran 11 shell commands,
   *  wrote 2 files"). Open: nothing (the rows speak). */
  groupLabel(group: ToolGroup): string {
    if (this.isGroupWorking(group)) {
      const running = group.tools.find(t => !t.result) ?? group.tools[group.tools.length - 1];
      const title = running.title || `${running.name}${running.paramPreview}`;
      if (!running.startedAt) return title;
      const secs = Math.max(0, Math.floor((Date.now() - running.startedAt) / 1000));
      return `${title} · ${secs}s`;
    }
    if (group.isOpen) return '';
    return ToolMessageComponent.mixLabel(group.tools);
  }

  /** Files the group wrote or edited, shown as links on the collapsed header so a
   *  written doc is one click away without opening the group (Ben, 2026-09-22:
   *  hunting the path through expanded rows was "a lot of noise to sift through").
   *  Deduplicated, first-touched order, capped like the icon strip. */
  static readonly FILE_CHIP_CAP = 4;
  static readonly WRITING_TOOLS = new Set(['Write', 'Edit', 'MultiEdit', 'NotebookEdit']);
  touchedFiles(group: ToolGroup): string[] {
    const seen: string[] = [];
    for (const t of group.tools) {
      if (!ToolMessageComponent.WRITING_TOOLS.has(t.name)) continue;
      const p = t.params.find(x => x.key === 'file_path' || x.key === 'notebook_path')?.value?.trim();
      if (p && !seen.includes(p)) seen.push(p);
    }
    return seen;
  }
  fileChips(group: ToolGroup): string[] {
    return this.touchedFiles(group).slice(0, ToolMessageComponent.FILE_CHIP_CAP);
  }
  hiddenFileCount(group: ToolGroup): number {
    return Math.max(0, this.touchedFiles(group).length - ToolMessageComponent.FILE_CHIP_CAP);
  }
  fileName(path: string): string {
    return path.split('/').pop() || path;
  }
  openTouchedFile(path: string, event: Event): void {
    event.stopPropagation();
    this.openFile.emit({ path, title: this.fileName(path) });
  }

  static mixLabel(tools: ToolCall[]): string {
    const counts = new Map<string, number>();
    for (const t of tools) counts.set(t.name, (counts.get(t.name) || 0) + 1);
    const clauses: { n: number; text: string }[] = [];
    const take = (names: string[], one: string, many: string) => {
      let n = 0;
      for (const nm of names) { n += counts.get(nm) || 0; counts.delete(nm); }
      if (n) clauses.push({ n, text: n === 1 ? one : many.replace('#', String(n)) });
    };
    take(['Bash'], 'ran 1 shell command', 'ran # shell commands');
    take(['Write'], 'wrote 1 file', 'wrote # files');
    take(['Edit', 'MultiEdit', 'NotebookEdit'], 'edited 1 file', 'edited # files');
    take(['Read'], 'read 1 file', 'read # files');
    take(['Grep', 'Glob'], 'ran 1 search', 'ran # searches');
    take(['WebFetch', 'WebSearch'], 'looked up 1 page', 'looked up # pages');
    take(['Agent', 'Task'], 'ran 1 agent', 'ran # agents');
    take(['ScheduleWakeup'], 'scheduled a wakeup', 'scheduled # wakeups');
    let rest = 0;
    for (const n of counts.values()) rest += n;
    if (rest) clauses.push({ n: rest, text: rest === 1 ? 'used 1 tool' : `used ${rest} tools` });
    clauses.sort((a, b) => b.n - a.n);
    const s = clauses.map(c => c.text).join(', ');
    return s ? s.charAt(0).toUpperCase() + s.slice(1) : '';
  }

  /** An AskUserQuestion is answerable only on the live (streaming) turn, single-select,
   *  and not already answered — historical/completed questions render static. */
  canAnswer(q: AskQuestion): boolean {
    return this.isStreaming && !q.multiSelect && !this.answered.has(q.question)
      && !this.presetMatches(q);
  }
  isAnswered(q: AskQuestion): boolean {
    return this.answered.has(q.question) || this.presetMatches(q);
  }
  selectedAnswer(q: AskQuestion): string | undefined {
    return this.answered.get(q.question)
      ?? (this.presetMatches(q) ? this.presetAnswer : undefined);
  }
  private presetMatches(q: AskQuestion): boolean {
    return !!this.presetAnswer && (q.options || []).some(o => o.label === this.presetAnswer);
  }
  selectOption(q: AskQuestion, opt: { label: string }): void {
    if (!this.canAnswer(q)) return;
    this.answered.set(q.question, opt.label);
    this.answer.emit({ question: q.question, answer: opt.label });
  }

  ngAfterViewChecked(): void {
    if (this.viewCheckedCount < 5) {
      this.viewCheckedCount++;
      this.processVegaBlocks();
      this.processFilePaths();
    }
  }

  /** Whether tool calls are expanded by default (power user setting or white-label override) */
  get toolCallsExpanded(): boolean {
    return localStorage.getItem('toolCallsExpanded') === 'true'
      || (window as any).env?.whiteLabel?.expandToolCalls === true;
  }

  /** True while this group is still accumulating tool calls during streaming */
  isGroupWorking(group: ToolGroup): boolean {
    if (!this.isStreaming) return false;
    const lastTool = group.tools[group.tools.length - 1];
    return !lastTool?.result;
  }

  /** True if any tool group is still working — suppresses the streaming dots */
  get hasWorkingGroup(): boolean {
    return this.parts.some(p => p.type === 'tool-group' && this.isGroupWorking(p.toolGroup!));
  }

  /** True if the last part is a thinking indicator — replaces the streaming dots */
  get hasTrailingThinking(): boolean {
    const last = this.parts[this.parts.length - 1];
    return last?.type === 'thinking';
  }

  /** The live spinner is the LAST part of a streaming turn; every other
   *  thinking part is narration that already happened and renders still. */
  isTrailingThinking(part: ContentPart): boolean {
    return this.isStreaming && this.parts[this.parts.length - 1] === part;
  }

  trackPart(index: number, part: ContentPart): string {
    return index + '-' + part.type;
  }

  private processVegaBlocks(): void {
    this.ngZone.runOutsideAngular(() => {
      const codeBlocks = this.el.nativeElement.querySelectorAll('pre code.language-vega-lite:not([data-processed]), pre code.language-vega:not([data-processed])');
      codeBlocks.forEach((block: HTMLElement) => {
        block.setAttribute('data-processed', 'true');
        this.renderVegaLite(block);
      });
    });
  }

  /**
   * Find file paths in tool result <pre> elements and param values, and make them clickable.
   * Matches absolute paths like /home/user/path/to/file.ext or ~/path/to/file.ext
   */
  private processFilePaths(): void {
    this.ngZone.runOutsideAngular(() => {
      const containers = this.el.nativeElement.querySelectorAll(
        '.tool-result-content pre:not([data-paths-processed]), .param-value:not([data-paths-processed])'
      );

      containers.forEach((container: HTMLElement) => {
        container.setAttribute('data-paths-processed', 'true');

        const filePathRegex = /((?:\/[\w.@+-]+)+\.\w+|~(?:\/[\w.@+-]+)+\.\w+)/g;

        const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT, {
          acceptNode: (node) => {
            const parent = node.parentElement;
            if (!parent || parent.closest('.file-path-btn')) return NodeFilter.FILTER_REJECT;
            if (filePathRegex.test(node.textContent || '')) {
              filePathRegex.lastIndex = 0;
              return NodeFilter.FILTER_ACCEPT;
            }
            return NodeFilter.FILTER_REJECT;
          }
        });

        const textNodes: Text[] = [];
        let node: Node | null;
        while ((node = walker.nextNode())) {
          textNodes.push(node as Text);
        }

        textNodes.forEach(textNode => {
          const text = textNode.textContent || '';
          const parent = textNode.parentElement;
          if (!parent) return;

          const matches: { path: string; start: number; end: number }[] = [];
          let match;
          filePathRegex.lastIndex = 0;
          while ((match = filePathRegex.exec(text)) !== null) {
            matches.push({ path: match[1], start: match.index, end: match.index + match[0].length });
          }
          if (matches.length === 0) return;

          const fragment = document.createDocumentFragment();
          let lastEnd = 0;

          matches.forEach(m => {
            if (m.start > lastEnd) {
              fragment.appendChild(document.createTextNode(text.slice(lastEnd, m.start)));
            }

            const btn = document.createElement('button');
            btn.className = 'file-path-btn';
            btn.textContent = m.path;
            btn.title = 'Open file';
            btn.addEventListener('click', (e) => {
              e.preventDefault();
              e.stopPropagation();
              const title = m.path.split('/').pop() || 'file';
              this.ngZone.run(() => {
                this.openFile.emit({ path: m.path, title });
              });
            });

            fragment.appendChild(btn);
            lastEnd = m.end;
          });

          if (lastEnd < text.length) {
            fragment.appendChild(document.createTextNode(text.slice(lastEnd)));
          }

          parent.replaceChild(fragment, textNode);
        });
      });
    });
  }

  /**
   * Check if content contains tool delimiters
   */
  hasToolCalls(): boolean {
    if (this.content?.includes('\x02TOOLJSON')) return true;
    if (this.content && /^TOOLJSON\{/m.test(this.content)) return true;
    if (this.content?.includes('[THINKING]')) return true;
    if (this.content && /(?<!`)\[TOOL_START\]🔧 Tool:/.test(this.content)) return true;
    if (this.hasTuiToolCalls(this.content)) return true;
    return false;
  }

  private static RE_TUI_TOOL = /^\s*[^\w\s]\s+([A-Z]\w*)\(/m;
  private static RE_TUI_COLLAPSED = /\(ctrl\+o to expand\)\s*$/m;

  private hasTuiToolCalls(content: string | undefined): boolean {
    if (!content) return false;
    return ToolMessageComponent.RE_TUI_TOOL.test(content) ||
           ToolMessageComponent.RE_TUI_COLLAPSED.test(content);
  }

  /**
   * Parse the content, extract tool calls, then group consecutive tools into ToolGroups.
   */
  private parseContent(): void {
    if (!this.content) {
      this.parts = [];
      return;
    }

    // During streaming, once we enter TUI parsing mode, stay there even if a
    // transient snapshot lacks the tool markers.  The REPLACE protocol delivers
    // full tmux snapshots and some catches miss the tool line mid-refresh.
    const isTui = this.hasTuiToolCalls(this.content) && !this.content.includes('\x02TOOLJSON');
    if (isTui) this.wasTuiMode = true;
    if (!this.isStreaming) this.wasTuiMode = false;

    if (!this.hasToolCalls() && !(this.isStreaming && this.wasTuiMode)) {
      this.parts = [{ type: 'text', content: this.content }];
      return;
    }

    // TUI format from organic relay (● ToolName(...) / ⎿ result)
    if (isTui || (this.isStreaming && this.wasTuiMode)) {
      this.parseTuiContent();
      return;
    }

    const parts: ContentPart[] = [];
    let currentPos = 0;
    const content = this.content;
    let lastToolCall: ToolCall | null = null;
    const pendingToolCalls: ToolCall[] = [];

    const fencePositions = this.findFencedCodeRanges(content);

    while (currentPos < content.length) {
      const jsonStart = content.indexOf('\x02TOOLJSON', currentPos);

      // Standalone result block (parallel tool calls emit results after all calls)
      const resultStart = content.indexOf('\x02RESULT\x03', currentPos);

      let legacyStart = -1;
      let searchFrom = currentPos;
      while (searchFrom < content.length) {
        const candidate = content.indexOf('[TOOL_START]', searchFrom);
        if (candidate === -1) break;
        const afterTag = content.slice(candidate + '[TOOL_START]'.length).trimStart();
        const precededByBacktick = candidate > 0 && content[candidate - 1] === '`';
        const inFence = fencePositions.some(([s, e]) => candidate >= s && candidate < e);
        if (afterTag.startsWith('🔧 Tool:') && !precededByBacktick && !inFence) {
          legacyStart = candidate;
          break;
        }
        searchFrom = candidate + 1;
      }

      let thinkingStart = -1;
      let thinkSearch = currentPos;
      while (thinkSearch < content.length) {
        const candidate = content.indexOf('[THINKING]', thinkSearch);
        if (candidate === -1) break;
        const precededByBacktick = candidate > 0 && content[candidate - 1] === '`';
        const inFence = fencePositions.some(([s, e]) => candidate >= s && candidate < e);
        if (!precededByBacktick && !inFence) {
          thinkingStart = candidate;
          break;
        }
        thinkSearch = candidate + 1;
      }

      const candidates = [
        { pos: jsonStart, type: 'json' as const },
        { pos: resultStart, type: 'result' as const },
        { pos: legacyStart, type: 'legacy' as const },
        { pos: thinkingStart, type: 'thinking' as const },
      ].filter(c => c.pos !== -1).sort((a, b) => a.pos - b.pos);

      if (candidates.length === 0) {
        const remaining = content.slice(currentPos).trim();
        if (remaining) {
          parts.push({ type: 'text', content: remaining });
        }
        break;
      }

      const next = candidates[0];

      if (next.pos > currentPos) {
        const textContent = content.slice(currentPos, next.pos).trim();
        if (textContent) {
          parts.push({ type: 'text', content: textContent });
        }
      }

      if (next.type === 'json') {
        const startTag = '\x02TOOLJSON';
        const jsonEnd = content.indexOf('\x03', next.pos);
        if (jsonEnd === -1) {
          if (this.isStreaming) {
            const partial = content.slice(next.pos + startTag.length).trim();
            if (partial) {
              const parsed = this.parseToolJson(partial);
              if (parsed) {
                lastToolCall = parsed.tool || null;
                parts.push(parsed);
              }
            }
          }
          break;
        }

        const jsonStr = content.slice(next.pos + startTag.length, jsonEnd).trim();
        if (jsonStr) {
          const parsed = this.parseToolJson(jsonStr);
          if (parsed) {
            lastToolCall = parsed.tool || null;
            parts.push(parsed);
          }
        }
        currentPos = jsonEnd + 1;

        if (pendingToolCalls.length > 0 && content.startsWith('\x02RESULT\x03', currentPos)) {
          // Parallel batch: queue this call, let the standalone result handler match in order
          if (lastToolCall) {
            pendingToolCalls.push(lastToolCall);
            lastToolCall = null;
          }
        } else if (content.startsWith('\x02RESULT\x03', currentPos)) {
          const RSTART = '\x02RESULT\x03';
          const REND = '\x02/RESULT\x03';
          const resultEnd = content.indexOf(REND, currentPos + RSTART.length);
          if (resultEnd === -1) {
            if (this.isStreaming && lastToolCall) {
              const partialResult = content.slice(currentPos + RSTART.length).trim();
              lastToolCall.result = partialResult + '...';
              lastToolCall = null;
              break;
            }
            lastToolCall = null;
            currentPos += RSTART.length;
          } else {
            const resultContent = content.slice(currentPos + RSTART.length, resultEnd).trim();
            if (lastToolCall) {
              lastToolCall.result = resultContent;
              lastToolCall = null;
            }
            currentPos = resultEnd + REND.length;
          }
        } else if (content.startsWith('[TOOL_RESULT]', currentPos)) {
          const resultEnd = this.findMatchingResultEnd(content, currentPos);
          if (resultEnd === -1) {
            if (this.isStreaming && lastToolCall) {
              const partialResult = content.slice(currentPos + '[TOOL_RESULT]'.length).trim();
              lastToolCall.result = partialResult + '...';
              lastToolCall = null;
              break;
            }
            lastToolCall = null;
            currentPos += '[TOOL_RESULT]'.length;
          } else {
            const resultContent = content.slice(currentPos + '[TOOL_RESULT]'.length, resultEnd).trim();
            if (lastToolCall) {
              lastToolCall.result = resultContent;
              lastToolCall = null;
            }
            currentPos = resultEnd + '[/TOOL_RESULT]'.length;
          }
        } else if (lastToolCall) {
          // No immediate result — queue for later (parallel tool calls)
          pendingToolCalls.push(lastToolCall);
          lastToolCall = null;
        }

      } else if (next.type === 'result') {
        // Standalone result block — from parallel tool calls where results come after all calls
        const RSTART = '\x02RESULT\x03';
        const REND = '\x02/RESULT\x03';
        const resultEnd = content.indexOf(REND, next.pos + RSTART.length);
        if (resultEnd === -1) {
          const target = pendingToolCalls.shift() || null;
          if (this.isStreaming && target) {
            const partialResult = content.slice(next.pos + RSTART.length).trim();
            target.result = partialResult + '...';
            break;
          }
          currentPos = next.pos + RSTART.length;
        } else {
          const resultContent = content.slice(next.pos + RSTART.length, resultEnd).trim();
          const target = pendingToolCalls.shift() || null;
          if (target) {
            target.result = resultContent;
          }
          currentPos = resultEnd + REND.length;
        }

      } else if (next.type === 'legacy') {
        const toolEnd = content.indexOf('[TOOL_END]', next.pos);
        if (toolEnd === -1) {
          if (this.isStreaming) {
            const toolContent = content.slice(next.pos + '[TOOL_START]'.length).trim();
            if (toolContent) {
              const toolCall = this.parseLegacyToolCall(toolContent);
              lastToolCall = toolCall;
              parts.push({ type: 'tool', tool: toolCall });
            }
          }
          break;
        }

        const toolContent = content.slice(next.pos + '[TOOL_START]'.length, toolEnd).trim();
        if (toolContent) {
          const toolCall = this.parseLegacyToolCall(toolContent);
          lastToolCall = toolCall;
          parts.push({ type: 'tool', tool: toolCall });
        }
        currentPos = toolEnd + '[TOOL_END]'.length;

      } else if (next.type === 'thinking') {
        const thinkingEnd = content.indexOf('[/THINKING]', next.pos + '[THINKING]'.length);
        if (thinkingEnd === -1) {
          // Still streaming thinking content
          if (this.isStreaming) {
            const partialThinking = content.slice(next.pos + '[THINKING]'.length).trim();
            if (partialThinking) {
              parts.push({ type: 'thinking', content: partialThinking });
            }
          }
          break;
        }

        const thinkingContent = content.slice(next.pos + '[THINKING]'.length, thinkingEnd).trim();
        if (thinkingContent) {
          parts.push({ type: 'thinking', content: thinkingContent });
        }
        currentPos = thinkingEnd + '[/THINKING]'.length;
      }
    }

    // Second pass: group consecutive tool parts into tool-groups
    const grouped: ContentPart[] = [];
    let i = 0;
    const expandedByDefault = this.toolCallsExpanded;
    while (i < parts.length) {
      if (parts[i].type === 'tool') {
        const tools: ToolCall[] = [];
        while (i < parts.length && parts[i].type === 'tool') {
          tools.push(parts[i].tool!);
          i++;
        }
        grouped.push({ type: 'tool-group', toolGroup: { tools, isOpen: expandedByDefault } });
      } else {
        grouped.push(parts[i]);
        i++;
      }
    }

    this.dedupeAskParts(grouped);
    this.parts = grouped;
  }

  /** One card per question: a turn can transiently carry the same AskUserQuestion
   *  twice — the mux-synthesized card (chunk) plus the reflow's own frame once the
   *  answered tool call enters the transcript. Keep the LAST occurrence (the
   *  reflow frame carries the canonical tool input). Mutates in place. */
  private dedupeAskParts(parts: ContentPart[]): void {
    // Only ADJACENT identical cards are the same question seen twice. Two
    // distinct permission prompts in one turn read "Do you want to proceed?"
    // both times, and a turn-wide dedupe kept only the last — the second
    // prompt looked like the already-answered first one, so nobody answered
    // it (Ben, games, 2026-09-11). Tool calls or text between them mean a
    // new question.
    const keyOf = (p: ContentPart) => (p.questions || []).map(q => `${q.context || ''}\n${q.question}`).join('\n');
    for (let idx = parts.length - 1; idx >= 1; idx--) {
      if (parts[idx].type !== 'ask') continue;
      let j = idx - 1;
      while (j >= 0 && parts[j].type === 'text' && !String(parts[j].content || '').trim()) j--;
      if (j >= 0 && parts[j].type === 'ask' && keyOf(parts[j]) === keyOf(parts[idx])) {
        parts.splice(j, 1); // keep the later (canonical) frame, drop its earlier twin
      }
    }
  }

  /**
   * Parse a [TOOL_JSON] block — structured data from the backend.
   */
  private parseToolJson(jsonStr: string): ContentPart | null {
    try {
      const data = JSON.parse(jsonStr);
      const toolName: string = data.name || 'Unknown';
      const toolInput: Record<string, any> = data.input || {};

      if (toolName === 'AskUserQuestion' && toolInput.questions) {
        const raw = toolInput.questions;
        const questions: AskQuestion[] = (Array.isArray(raw) ? raw : [raw]).map((q: any) => ({
          header: q.header,
          question: q.question,
          context: q.context,
          options: (q.options || []).map((o: any) => ({
            label: o.label,
            description: o.description
          })),
          multiSelect: q.multiSelect
        }));
        return { type: 'ask', questions };
      }

      const params: { key: string; value: string }[] = [];
      for (const [key, value] of Object.entries(toolInput)) {
        params.push({
          key,
          value: typeof value === 'string' ? value : JSON.stringify(value)
        });
      }

      let paramPreview = '';
      if (toolName === 'ScheduleWakeup') {
        const delay = toolInput.delaySeconds || toolInput.delay_seconds;
        const reason = toolInput.reason || '';
        if (delay) {
          const fireAt = new Date(Date.now() + delay * 1000);
          const timeStr = fireAt.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
          const mins = Math.round(delay / 60);
          paramPreview = `: waking at ${timeStr} (in ${mins}m)${reason ? ' — ' + reason : ''}`;
        }
      } else {
        const primaryParam = params.find(p =>
          ['description', 'caption', 'file_path', 'command', 'pattern', 'prompt', 'content', 'query'].includes(p.key)
        );
        if (primaryParam) {
          const displayValue = primaryParam.value.length > 60
            ? primaryParam.value.substring(0, 60) + '...'
            : primaryParam.value;
          paramPreview = `: ${displayValue}`;
        }
      }

      const iconSvg = this.iconMap[toolName] || this.iconMap['default'];
      const description = typeof toolInput.description === 'string' ? toolInput.description.trim() : '';

      return {
        type: 'tool',
        tool: {
          name: toolName,
          icon: this.sanitizer.bypassSecurityTrustHtml(iconSvg),
          paramPreview,
          params,
          isOpen: false,
          title: description || undefined
        }
      };
    } catch {
      return null;
    }
  }

  /**
   * Parse TUI-format tool calls from organic relay content.
   * Recognizes ● ToolName(args) and ⎿ result patterns.
   */
  private parseTuiContent(): void {
    const lines = this.content.split('\n');
    const parts: ContentPart[] = [];
    const expandedByDefault = this.toolCallsExpanded;
    let textBuf: string[] = [];
    let i = 0;

    const flushText = () => {
      const t = textBuf.join('\n').trim();
      if (t) parts.push({ type: 'text', content: t });
      textBuf = [];
    };

    while (i < lines.length) {
      const line = lines[i];
      const toolMatch = line.match(/^\s*[^\w\s]\s+([A-Z]\w*)\((.*)$/);

      if (toolMatch) {
        flushText();
        const toolName = toolMatch[1];
        let argText = toolMatch[2];
        i++;
        // Collect continuation lines (indented, before ⎿ or next marker)
        while (i < lines.length) {
          const s = lines[i].trimStart();
          if (s.startsWith('⎿') || /^\s*[^\w\s]\s+[A-Z]/.test(lines[i]) || /^\s*[●◉○◎◌◐◑◒◓◔◕◖◗⊙⊚⊛]\s+\w/.test(lines[i]) || /^\s*\S\s+.*…/.test(lines[i])) break;
          if (/^\s*×\s+\d+\s+tool/.test(lines[i]) || /\(ctrl\+o to expand\)\s*$/.test(lines[i])) break;
          argText += '\n' + lines[i];
          i++;
        }
        // Trim trailing ) from args
        argText = argText.replace(/\)\s*$/, '').trim();

        // Collect result (⎿ lines)
        let result = '';
        if (i < lines.length && lines[i].trimStart().startsWith('⎿')) {
          result = lines[i].replace(/^\s*⎿\s?/, '');
          i++;
          while (i < lines.length) {
            const s = lines[i].trimStart();
            if (/^\s*[^\w\s]\s+[A-Z]/.test(lines[i]) || /^\s*[●◉○◎◌◐◑◒◓◔◕◖◗⊙⊚⊛]\s+\w/.test(lines[i]) || /^\s*\S\s+.*…/.test(lines[i]) || /^\s*×\s+\d+\s+tool/.test(lines[i]) || /\(ctrl\+o to expand\)\s*$/.test(lines[i])) break;
            if (s.startsWith('⎿')) {
              result += '\n' + s.replace(/^⎿\s?/, '');
            } else {
              result += '\n' + lines[i];
            }
            i++;
          }
          result = result.trim();
        }

        const paramPreview = argText.length > 60 ? `: ${argText.substring(0, 60)}...` : argText ? `: ${argText}` : '';
        const iconSvg = this.iconMap[toolName] || this.iconMap['default'];

        const tool: ToolCall = {
          name: toolName,
          icon: this.sanitizer.bypassSecurityTrustHtml(iconSvg),
          paramPreview,
          params: argText ? [{ key: 'input', value: argText }] : [],
          result: result || undefined,
          isOpen: false,
        };
        parts.push({ type: 'tool', tool });
      } else if (/\(ctrl\+o to expand\)\s*$/.test(line)) {
        flushText();
        const summary = line.trim().replace(/^\s*[^\w\s]\s*/, '').replace(/\s*\(ctrl\+o to expand\)\s*$/, '');
        const verbMap: Record<string, string> = {
          'read': 'Read', 'reading': 'Read', 'searched': 'Grep', 'searching': 'Grep',
          'edited': 'Edit', 'editing': 'Edit', 'wrote': 'Write', 'writing': 'Write',
          'listed': 'Glob', 'listing': 'Glob', 'recalled': 'Read',
          'ran': 'Bash', 'running': 'Bash',
        };
        const firstWord = summary.split(/\s/)[0].toLowerCase();
        const toolName = verbMap[firstWord] || 'default';
        const iconSvg = this.iconMap[toolName] || this.iconMap['default'];
        // Collect result (⎿ lines) after collapsed summary
        let result = '';
        if (i + 1 < lines.length && lines[i + 1].trimStart().startsWith('⎿')) {
          i++;
          result = lines[i].replace(/^\s*⎿\s?/, '');
          i++;
          while (i < lines.length) {
            const s = lines[i].trimStart();
            if (/^\s*[^\w\s]\s+[A-Z]/.test(lines[i]) || /^\s*[●◉○◎◌◐◑◒◓◔◕◖◗⊙⊚⊛]\s+\w/.test(lines[i]) || /^\s*\S\s+.*…/.test(lines[i]) || /^\s*×\s+\d+\s+tool/.test(lines[i]) || /\(ctrl\+o to expand\)\s*$/.test(lines[i])) break;
            if (s.startsWith('⎿')) {
              result += '\n' + s.replace(/^⎿\s?/, '');
            } else {
              result += '\n' + lines[i];
            }
            i++;
          }
          result = result.trim();
        } else {
          i++;
        }
        const tool: ToolCall = {
          name: toolName,
          icon: this.sanitizer.bypassSecurityTrustHtml(iconSvg),
          paramPreview: `: ${summary}`,
          params: [],
          result: result || undefined,
          isOpen: false,
        };
        parts.push({ type: 'tool', tool });
      } else if (/^\s*×\s+\d+\s+tool/.test(line)) {
        // "× 32 tool uses (ctrl+o to expand)" — skip
        i++;
      } else if (/^\s*\S\s+.*…(\s*\(.*\))?\s*$/.test(line)) {
        // Spinner line — any single char prefix followed by text containing …
        // Allows trailing stats in parens: "✻ Skedaddling… (5s · ↓ 111 tokens)"
        // Character-agnostic: the TUI cycles through ✻·✤●*+ etc.
        flushText();
        parts.push({ type: 'thinking', content: line.trim() });
        i++;
      } else if (/^\s*[^\w\s●◉○◎◌◐◑◒◓◔◕◖◗⊙⊚⊛]\s+\w/.test(line) && !/…/.test(line) && /^\s*[^\w\s]\s/.test(line) && line.trim().length < 40) {
        // Completion marker (e.g. "✻ Baked for 16s") — skip, not content
        // Excludes ● response bullets and their animated variants
        i++;
      } else {
        // Strip circle response markers (●◉○◎◌◐◑ etc.) — the web app's message cards replace their role
        textBuf.push(line.replace(/^\s*[●◉○◎◌◐◑◒◓◔◕◖◗⊙⊚⊛]\s*/, ''));
        i++;
      }
    }
    flushText();

    // Group consecutive tools
    const grouped: ContentPart[] = [];
    let j = 0;
    while (j < parts.length) {
      if (parts[j].type === 'tool') {
        const tools: ToolCall[] = [];
        while (j < parts.length && parts[j].type === 'tool') {
          tools.push(parts[j].tool!);
          j++;
        }
        grouped.push({ type: 'tool-group', toolGroup: { tools, isOpen: expandedByDefault } });
      } else {
        grouped.push(parts[j]);
        j++;
      }
    }

    this.dedupeAskParts(grouped);

    // Anti-flicker: during streaming, tmux snapshots can catch mid-refresh
    // states. Protect against two failure modes:
    // 1. Fewer stable parts (tool groups vanish momentarily)
    // 2. Tool groups replaced by text parts (content falls through to text bucket)
    // In both cases, keep the previous stable parts and only update thinking.
    if (this.isStreaming && this.parts.length > 0) {
      const prevToolGroups = this.parts.filter(p => p.type === 'tool-group').length;
      const newToolGroups = grouped.filter(p => p.type === 'tool-group').length;
      const prevStable = this.parts.filter(p => p.type !== 'thinking');
      const newStable = grouped.filter(p => p.type !== 'thinking');

      const isRegression = newStable.length < prevStable.length || newToolGroups < prevToolGroups;

      if (isRegression) {
        // Only update the thinking indicator, keep stable parts
        const newThinking = grouped.find(p => p.type === 'thinking');
        const oldThinkingIdx = this.parts.findIndex(p => p.type === 'thinking');
        if (newThinking) {
          if (oldThinkingIdx >= 0) {
            this.parts[oldThinkingIdx].content = newThinking.content;
          } else {
            this.parts.push(newThinking);
          }
        } else if (oldThinkingIdx >= 0) {
          this.parts.splice(oldThinkingIdx, 1);
        }
        return;
      }

      // Structure matches — update existing objects in place to avoid
      // Angular re-rendering DOM nodes from new object references
      if (this.parts.length === grouped.length) {
        for (let k = 0; k < grouped.length; k++) {
          if (this.parts[k].type === grouped[k].type) {
            if (grouped[k].type === 'thinking') {
              this.parts[k].content = grouped[k].content;
            } else if (grouped[k].type === 'text') {
              this.parts[k].content = grouped[k].content;
            } else if (grouped[k].type === 'tool-group') {
              this.parts[k].toolGroup!.tools = grouped[k].toolGroup!.tools;
            }
          } else {
            this.parts[k] = grouped[k];
          }
        }
        return;
      }
    }

    this.parts = grouped;
  }

  private findFencedCodeRanges(content: string): [number, number][] {
    const ranges: [number, number][] = [];
    const fenceRegex = /^(`{3,})[^\n]*$/gm;
    let match: RegExpExecArray | null;
    let openPos = -1;
    let openFence = '';
    while ((match = fenceRegex.exec(content)) !== null) {
      if (openPos === -1) {
        openPos = match.index;
        openFence = match[1];
      } else if (match[1].length >= openFence.length) {
        ranges.push([openPos, match.index + match[0].length]);
        openPos = -1;
        openFence = '';
      }
    }
    if (openPos !== -1) {
      ranges.push([openPos, content.length]);
    }
    return ranges;
  }

  private findMatchingResultEnd(content: string, resultStartPos: number): number {
    const OPEN = '[TOOL_RESULT]';
    const CLOSE = '[/TOOL_RESULT]';
    const searchStart = resultStartPos + OPEN.length;

    // \x02 only appears in backend-emitted markers, never in file content.
    // The next \x02TOOLJSON marks the scope boundary. The LAST
    // [/TOOL_RESULT] before that boundary is the outer close tag.
    const nextToolCall = content.indexOf('\x02TOOLJSON', searchStart);
    const scopeEnd = nextToolCall !== -1 ? nextToolCall : content.length;
    const lastClose = content.lastIndexOf(CLOSE, scopeEnd - 1);
    if (lastClose >= searchStart) return lastClose;

    return -1;
  }

  /**
   * Parse legacy [TOOL_START] format.
   */
  private parseLegacyToolCall(toolContent: string): ToolCall {
    let toolName = 'Unknown';
    const params: { key: string; value: string }[] = [];

    const toolMatch = toolContent.match(/🔧 Tool: (\w+)/);
    if (toolMatch) {
      toolName = toolMatch[1];
    }

    const lines = toolContent.split('\n').slice(1);
    lines.forEach(line => {
      const paramMatch = line.match(/^\s+(\w+):\s*(.+)$/);
      if (paramMatch) {
        params.push({ key: paramMatch[1], value: paramMatch[2] });
      }
    });

    const primaryParam = params.find(p =>
      ['description', 'file_path', 'command', 'pattern', 'prompt', 'content'].includes(p.key)
    );
    let paramPreview = '';
    if (primaryParam) {
      const displayValue = primaryParam.value.length > 60
        ? primaryParam.value.substring(0, 60) + '...'
        : primaryParam.value;
      paramPreview = `: ${displayValue}`;
    }

    const iconSvg = this.iconMap[toolName] || this.iconMap['default'];

    return {
      name: toolName,
      icon: this.sanitizer.bypassSecurityTrustHtml(iconSvg),
      paramPreview,
      params,
      isOpen: false
    };
  }

  toggleTool(tool: ToolCall): void {
    tool.isOpen = !tool.isOpen;
    if (tool.isOpen) {
      this.ngZone.runOutsideAngular(() => {
        setTimeout(() => this.processFilePaths(), 0);
      });
    }
  }

  toggleGroup(group: ToolGroup): void {
    group.isOpen = !group.isOpen;
    if (group.isOpen) {
      this.ngZone.runOutsideAngular(() => {
        setTimeout(() => this.processFilePaths(), 0);
      });
    }
  }

  private async renderVegaLite(block: HTMLElement): Promise<void> {
    try {
      const spec = JSON.parse(block.textContent || '{}');
      const preElement = block.parentElement;
      if (!preElement) return;

      const vizContainer = document.createElement('div');
      vizContainer.className = 'vega-viz-container';

      const isDark = document.documentElement.getAttribute('data-theme') === 'dark';

      await embed(vizContainer, spec, {
        actions: { export: true, source: false, compiled: false, editor: false },
        theme: isDark ? 'dark' : 'vox'
      });

      preElement.parentElement?.replaceChild(vizContainer, preElement);
    } catch (error) {
      console.error('Failed to render Vega chart:', error);
    }
  }
}
